import jsPDF from "jspdf";
import "jspdf-autotable";
import Logo from "../../assets/nhclogo.png";

const PDF_STYLES = {
    colors: {
        primary: '#1e85e5',
        textDark: '#1a1a1a',
        textMuted: '#666666',
        textLight: '#888888',
        borderLight: '#e0e0e0',
        backgroundAlt: '#f8f9fa'
    },
    fontSize: {
        title: 16,
        subtitle: 13,
        sectionHeader: 12,
        body: 10,
        tableCell: 8,
        footer: 8
    }
};

export const formatData = (report) => {
    let data = [];
    Object.keys(report).forEach((item) => {
        if (item === "spendingAccounts" || item === "enrollmentFee" || item === "teladoc" || item === "corehealth") {
            data.push(...report[item]);
        } else {
            if (item === "teladocTotal" || item === "corehealthTotal" || item === "spendingAccountsTotal" || item === "enrollmentFeeTotal") {
                if (parseFloat(report[item]) > 0) {
                    data.push({ [item]: report[item] });
                }
            } else {
                data.push({ [item]: report[item] });
            }
        }
    });
    return data;
};

export const generatePDF = (report, fromDate, toDate) => {
    const doc = new jsPDF();
    const docTitle = "Commission Report";

    jsPDF.autoTableSetDefaults({
        theme: "striped",
        tableWidth: "auto",
        columnWidth: "auto",
        showHead: "everyPage",
        rowPageBreak: "avoid",
        pageBreak: "auto",
        margin: { top: 40, bottom: 25 },
        headStyles: {
            fillColor: PDF_STYLES.colors.primary,
            textColor: '#ffffff',
            fontSize: PDF_STYLES.fontSize.tableCell,
            fontStyle: 'bold'
        },
        alternateRowStyles: {
            fillColor: PDF_STYLES.colors.backgroundAlt
        },
        styles: {
            overflow: "linebreak",
            cellPadding: 4,
            fontSize: PDF_STYLES.fontSize.tableCell,
            lineColor: PDF_STYLES.colors.borderLight,
            lineWidth: 0.25,
            textColor: PDF_STYLES.colors.textDark
        },
        columnStyles: { 1: { halign: "center", cellWidth: 20 } },
        didDrawPage: function (data) {
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();

            // header strip
            doc.setFillColor(PDF_STYLES.colors.primary);
            doc.rect(0, 0, pageWidth, 6, 'F');

            // logo
            doc.addImage(Logo, "PNG", 160, 12, 0, 12);

            // footer
            doc.setFontSize(PDF_STYLES.fontSize.footer);
            doc.setTextColor(PDF_STYLES.colors.textLight);
            doc.text(docTitle, 15, pageHeight - 10);
            doc.text(
                `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${doc.internal.getNumberOfPages()}`,
                pageWidth - 15,
                pageHeight - 10,
                { align: "right" }
            );
        },
    });

    const width = doc.internal.pageSize.getWidth();

    // title section
    doc.setFontSize(PDF_STYLES.fontSize.title);
    doc.setTextColor(PDF_STYLES.colors.textDark);
    doc.text(report.distroAccountName, width / 2, 30, { align: "center" });

    doc.setFontSize(PDF_STYLES.fontSize.subtitle);
    doc.setTextColor(PDF_STYLES.colors.primary);
    doc.text(`Commission Report - ${report.company || report.agentReportingCode}`, width / 2, 38, { align: "center" });

    doc.setFontSize(PDF_STYLES.fontSize.body);
    doc.setTextColor(PDF_STYLES.colors.textMuted);
    doc.text(`${fromDate} - ${toDate}`, width / 2, 46, { align: "center" });

    const tableColumnSummary = ["Type", "Amount"];
    const summaryRows = [];

    let summaryData = [];
    if (parseFloat(report.spendingAccountsTotal) > 0) {
        summaryData = ["Spending Accounts Total", `$${report.spendingAccountsTotal}`];
        summaryRows.push(summaryData);
    }

    if (parseFloat(report.enrollmentFeeTotal) > 0) {
        let enrollmentFee = ["Enrollment Fee Total", `$${report.enrollmentFeeTotal}`];
        summaryRows.push(enrollmentFee);
    }

    if (parseFloat(report.teladocTotal) > 0) {
        let teladoc = ["Teladoc Total", `$${report.teladocTotal}`];
        summaryRows.push(teladoc);
    }

    if (parseFloat(report.corehealthTotal) > 0) {
        let corehealth = ["Corehealth Total", `$${report.corehealthTotal}`];
        summaryRows.push(corehealth);
    }

    summaryRows.push(["Total", `$${report.total}`]);

    doc.setFontSize(PDF_STYLES.fontSize.sectionHeader);
    doc.setTextColor(PDF_STYLES.colors.primary);
    doc.text("Summary Report", 15, 60);
    doc.autoTable({
        columns: tableColumnSummary,
        body: summaryRows,
        startY: 65,
        columnStyles: { 1: { halign: 'right' } },
        didParseCell: function (data) {
            if (data.section === 'head' && data.column.index === 1) {
                data.cell.styles.halign = 'right';
            }

            if (data.row.index === summaryRows.length - 1) {
                data.cell.styles.fontStyle = 'bold';
            }
        }
    });

    let finalY = doc.previousAutoTable.finalY;
    const tableHeaders = ["Description", "Amount", "Date"];

    if (report.spendingAccounts.length > 0) {
        const spendingAccountsRows = [];

        report.spendingAccounts.forEach((item) => {
            const spendingAccountsData = [
                item.description,
                `$${item.amount}`,
                item.date,
            ];
            spendingAccountsRows.push(spendingAccountsData);
        });

        finalY = doc.previousAutoTable.finalY;

        doc.setFontSize(PDF_STYLES.fontSize.sectionHeader);
        doc.setTextColor(PDF_STYLES.colors.primary);
        doc.text("Spending Accounts Report", 15, finalY + 15);
        doc.autoTable({
            columns: tableHeaders,
            body: spendingAccountsRows,
            startY: finalY + 20,
            columnStyles: { 1: { halign: 'right' } }
        });
    }

    if (report.enrollmentFee.length > 0) {
        const enrollmentFeeRows = [];

        report.enrollmentFee.forEach((item) => {
            const enrollmentFeeData = [
                item.description,
                `$${item.amount}`,
                item.date,
            ];
            enrollmentFeeRows.push(enrollmentFeeData);
        });

        finalY = doc.previousAutoTable.finalY;

        doc.setFontSize(PDF_STYLES.fontSize.sectionHeader);
        doc.setTextColor(PDF_STYLES.colors.primary);
        doc.text("Enrollment Fee Report", 15, finalY + 15);
        doc.autoTable({
            columns: tableHeaders,
            body: enrollmentFeeRows,
            startY: finalY + 20,
            columnStyles: { 1: { halign: 'right' } }
        });
    }

    if (report.teladoc.length > 0) {
        const teladocRows = [];

        report.teladoc.forEach((item) => {
            const teladocData = [
                item.description,
                `$${item.amount}`,
                item.date,
            ];
            teladocRows.push(teladocData);
        });

        finalY = doc.previousAutoTable.finalY;

        doc.setFontSize(PDF_STYLES.fontSize.sectionHeader);
        doc.setTextColor(PDF_STYLES.colors.primary);
        doc.text("Teladoc Report", 15, finalY + 15);
        doc.autoTable({
            columns: tableHeaders,
            body: teladocRows,
            startY: finalY + 20,
            columnStyles: { 1: { halign: 'right' } }
        });
    }

    if (report.corehealth.length > 0) {
        const corehealthRows = [];

        report.corehealth.forEach((item) => {
            const corehealthData = [
                item.description,
                `$${item.amount}`,
                item.date,
            ];
            corehealthRows.push(corehealthData);
        });

        finalY = doc.previousAutoTable.finalY;

        doc.setFontSize(PDF_STYLES.fontSize.sectionHeader);
        doc.setTextColor(PDF_STYLES.colors.primary);
        doc.text("Corehealth Report", 15, finalY + 15);
        doc.autoTable({
            columns: tableHeaders,
            body: corehealthRows,
            startY: finalY + 20,
            columnStyles: { 1: { halign: 'right' } }
        });
    }

    const date = Date().split(" ");
    const dateStr = date[0] + date[1] + date[2] + date[3] + date[4];

    // we define the name of our PDF file.
    doc.save(`Commission Report - ${report.company || report.agentReportingCode} ${dateStr}.pdf`);
};
